import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom'

import { type AllDecksCount, api, type SessionCard, type SessionScore } from '../api'
import { DeckImage } from '../components/DeckImage'
import { ProblemBanner } from '../components/Problem'
import { type RandomPick, pickRandomDeck } from '../randomDeck'

const PORTIONS = [
  { value: 'quarter', label: 'A quarter' },
  { value: 'half', label: 'Half' },
  { value: 'three_quarters', label: 'Three quarters' },
  { value: 'all', label: 'Everything' },
]

const ALL_DECKS_COUNTS: AllDecksCount[] = [10, 25, 50]

type Phase = 'choosing' | 'answering' | 'revealed' | 'scored'

/**
 * Scored quiz (spec §6.2, §8.2).
 *
 * Grading happens server-side: the answer arrives only in the response to the answer
 * that was submitted, and the running score is never shown until the end.
 */
export default function Test() {
  // Keyed by deck so that moving to another deck ("Another random deck") starts fresh.
  const { deckId = '' } = useParams()
  return <TestSession key={deckId} />
}

function TestSession() {
  const { deckId = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  // "/test/all" is the mixed test across every eligible deck; there is no deck id.
  const isAll = useMatch('/test/all') !== null
  const backTo = isAll ? '/decks' : `/decks/${deckId}`
  const randomPick = (location.state as { randomPick?: RandomPick } | null)?.randomPick
  const [phase, setPhase] = useState<Phase>('choosing')
  const [noOtherDeck, setNoOtherDeck] = useState(false)
  const [portion, setPortion] = useState('half')
  const [count, setCount] = useState<AllDecksCount>(25)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [card, setCard] = useState<SessionCard | null>(null)
  const [progress, setProgress] = useState({ answered: 0, total: 0 })
  const [reveal, setReveal] = useState<{ back: string; correct: boolean } | null>(null)
  const [score, setScore] = useState<SessionScore | null>(null)
  const [error, setError] = useState<unknown>(null)

  const start = useCallback(() => {
    setError(null)
    const request = isAll
      ? api.startSession({ scope: 'all', mode: 'test', count })
      : api.startSession({ deckId, mode: 'test', portion, shuffle: true })
    request
      .then((session) => {
        setSessionId(session.sessionId)
        setCard(session.card)
        setProgress({ answered: 0, total: session.total })
        setPhase('answering')
      })
      .catch(setError)
  }, [deckId, portion, isAll, count])

  const submit = useCallback(
    (payload: { selfGrade?: string; choiceIndex?: number }) => {
      if (!sessionId || !card) return
      api
        .answer(sessionId, { cardId: card.id, ...payload })
        .then((result) => {
          setReveal({ back: result.back, correct: result.correct })
          setProgress({ answered: result.answered, total: result.total })
          setCard(result.next)
          setPhase('revealed')
        })
        .catch(setError)
    },
    [sessionId, card],
  )

  const advance = useCallback(() => {
    setReveal(null)
    if (card) {
      setPhase('answering')
      return
    }
    if (!sessionId) return
    api
      .complete(sessionId)
      .then((result) => {
        setScore(result)
        setPhase('scored')
      })
      .catch(setError)
  }, [card, sessionId])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (phase === 'answering' && card?.choices) {
        const index = Number(event.key) - 1
        if (index >= 0 && index < card.choices.length) submit({ choiceIndex: index })
      } else if (phase === 'revealed' && event.key === ' ') {
        event.preventDefault()
        advance()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, card, submit, advance])

  if (phase === 'choosing') {
    return (
      <>
        <h1>{isAll ? 'Test across all decks' : 'Test yourself'}</h1>
        <ProblemBanner error={error} />
        {!isAll && randomPick?.deckId === deckId ? (
          <p className="muted">Randomly picked: {randomPick.title}</p>
        ) : null}
        {isAll ? (
          <>
            <p className="muted">How many cards, drawn at random from every deck?</p>
            <div className="row">
              {ALL_DECKS_COUNTS.map((option) => (
                <button
                  key={option}
                  className={option === count ? 'primary' : ''}
                  aria-pressed={option === count}
                  onClick={() => setCount(option)}
                >
                  {option} cards
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="muted">How much of the deck?</p>
            <div className="row">
              {PORTIONS.map((option) => (
                <button
                  key={option.value}
                  className={option.value === portion ? 'primary' : ''}
                  onClick={() => setPortion(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>
        )}
        <p className="row">
          <button className="primary" onClick={start}>
            Start
          </button>
          <Link className="button" to={backTo}>
            Cancel
          </Link>
        </p>
      </>
    )
  }

  if (phase === 'scored' && score) {
    return (
      <>
        <h1>
          {score.correct} of {score.total} ({score.percent}%)
        </h1>
        <p className="muted">Finished in {Math.round(score.durationSeconds / 60)} min.</p>
        <table>
          <tbody>
            {score.breakdown.map((outcome) => (
              <tr key={outcome.cardId}>
                <td>{outcome.correct ? '✓' : '✗'}</td>
                <td>{outcome.front}</td>
                <td className="muted">{outcome.back}</td>
                {isAll ? <td className="muted">{outcome.deckTitle}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="row">
          <Link className="button primary" to={backTo}>
            {isAll ? 'Back to decks' : 'Back to deck'}
          </Link>
          {!isAll && randomPick ? (
            <button
              onClick={() => {
                api
                  .listDecks()
                  .then(({ decks }) => {
                    const next = pickRandomDeck(decks, deckId)
                    if (!next) {
                      setNoOtherDeck(true)
                      return
                    }
                    navigate(`/decks/${next.deckId}/test`, {
                      state: { randomPick: { deckId: next.deckId, title: next.title } },
                    })
                  })
                  .catch(setError)
              }}
            >
              Another random deck
            </button>
          ) : null}
        </p>
        {noOtherDeck ? <p className="muted">No other decks are eligible for a random test.</p> : null}
      </>
    )
  }

  return (
    <>
      <ProblemBanner error={error} />
      <div className="progress" aria-hidden="true">
        <span style={{ width: `${(progress.answered / Math.max(progress.total, 1)) * 100}%` }} />
      </div>
      {/* Progress only — the score stays hidden until the end (spec §8.2). */}
      <p className="muted">
        {progress.answered} of {progress.total} answered
      </p>

      {phase === 'revealed' && reveal ? (
        <>
          <article className="card flashcard">
            <p>
              <strong>{reveal.correct ? 'Correct' : 'Not quite'}</strong>
            </p>
            <p>{reveal.back}</p>
          </article>
          <button className="primary" onClick={advance}>
            {card ? 'Next card' : 'Finish and score'}
          </button>
          <p className="keyhint">Press space to continue.</p>
        </>
      ) : null}

      {phase === 'answering' && card ? (
        <>
          <article className="card flashcard">
            {card.deckTitle ? <p className="muted">From: {card.deckTitle}</p> : null}
            <p>{card.front}</p>
            {card.hint ? <p className="muted">Hint: {card.hint}</p> : null}
            {card.image ? <DeckImage name={card.image} /> : null}
          </article>

          {card.choices ? (
            <div className="choices">
              {card.choices.map((choice, index) => (
                <button key={choice} onClick={() => submit({ choiceIndex: index })}>
                  <kbd>{index + 1}</kbd>
                  {choice}
                </button>
              ))}
            </div>
          ) : (
            <div className="row">
              <button onClick={() => submit({ selfGrade: 'correct' })}>I knew it</button>
              <button onClick={() => submit({ selfGrade: 'incorrect' })}>I did not</button>
            </div>
          )}
          {card.choices ? <p className="keyhint">Press 1–4 to answer.</p> : null}
        </>
      ) : null}
    </>
  )
}
